import { SerializedData, Store } from "bernie-core";
import { action, observable } from "mobx";
import { Logger } from "bernie-logger";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";

export interface FlexModuleModel {
  responsePending: boolean;
  empty: boolean;
  error: boolean;
  model: object;
  isUsed?: boolean; // Temporary workaround to avoid serializing model in composition-path
  flexComponentRef?: FlexComponent; // Temporary workaround to avoid serializing model in composition-path
}

/**
 * Store that contains Flex Microservice model responses
 * returned by compositor in as a Map, accessible by module id
 *
 * Only populated through template-rendering-path.
 */
export class FlexModuleModelStore extends Store {
  @observable
  public modelMap: Map<string, FlexModuleModel>; // this is a Map because a plain object is not as observable
  private modelMapAsObject: object;

  public constructor(state: SerializedData, logger: Logger) {
    super(state, logger);
    this.modelMap = new Map();
  }

  public hydrate(data: SerializedData): void {
    if (!data?.modelMapAsObject) {
      return;
    }

    // Map isn't serializable: convert modelMapAsObject back to Map
    Object.getOwnPropertyNames(data.modelMapAsObject).forEach((key) => {
      this.modelMap.set(key, data.modelMapAsObject[key]);
    });
  }

  public getModel(id: string) {
    const item = this.modelMap.get(id);

    if (!item) {
      return null;
    }

    // Temporary workaround to avoid serializing model in composition-path if module is using this store
    // TODO: Clean up when 100% in FlexTemplateRenderer
    item.isUsed = true;

    return item.model;
  }

  @action
  public updateModelMap(id: string, flexModuleModel: FlexModuleModel, flexComponent?: FlexComponent) {
    if (!id) {
      return;
    }

    this.modelMap.set(id, { ...flexModuleModel, flexComponentRef: flexComponent });
  }

  public toJSON(): SerializedData {
    // Map isn't serializable: convert modelMap to modelMapAsObject
    this.modelMapAsObject = {};
    this.modelMap.forEach((value, key) => {
      if (!value.isUsed) {
        return;
      }

      const { flexComponentRef, isUsed, ...importantThings } = value;

      // Temporary workaround to avoid serializing model in composition-path if module is using this store
      // TODO: Clean up when 100% in FlexTemplateRenderer
      // includes hack to exclude hotels and wizard module's model from deletion as it causes a strange bug during hydration
      if (
        flexComponentRef &&
        (!flexComponentRef.module ||
          (flexComponentRef.module &&
            flexComponentRef.name !== "hotels" &&
            flexComponentRef.name !== "wizard-flight-pwa" &&
            flexComponentRef.name !== "wizard-hotel-pwa-v2" &&
            flexComponentRef.name !== "wizard-cruise-pwa" &&
            flexComponentRef.name !== "wizard-package-pwa" &&
            flexComponentRef.name !== "wizard-thirdPartyPackage-pwa" &&
            flexComponentRef.name !== "wizard-car-pwa" &&
            flexComponentRef.name !== "wizard-vr-pwa" &&
            flexComponentRef.name !== "te-neighborhood"))
      ) {
        flexComponentRef.model = {};
      }

      this.modelMapAsObject[key] = importantThings;
    });

    return super.toJSON(["modelMap"]);
  }
}
